import type { Bid, GameState } from '../types';
import type { ValidAction } from '../../multiplayer/types';
import type { ExecutionContext } from '../types/execution';
import type { RandomGenerator } from './hand-sampler';
import { matchEquityBidThresholdDelta, matchEquityMultiplier } from './match-equity';
import { selectNearEqual, scoreToleranceForTemperature } from './near-equal-select';
import {
  evaluateBidActions as evaluateBidActionsCore,
  evaluatePlayActions as evaluatePlayActionsCore,
  type MonteCarloConfig,
  type ProfiledSearchOptions,
  type ActionEvaluation,
} from './monte-carlo';
import { buildConstraints } from './constraint-tracker';
import { evaluateDeclarationActions } from './hand-strength';
import { createExecutionContext } from '../types/execution';
import { actionToId } from '../core/actions';
import type { SerializableDifficultyProfile } from './difficulty-types';
import type {
  AIActionAnalysis,
  AIActionChoiceMechanism,
  AIPairedPlayConfidence,
  AIStrategy,
} from './types';
import type { BehaviorModel } from './behavior-model.js';
import { shouldUseBehaviorWeights } from './adaptive-learning.js';
import {
  buildBidEvidence,
  buildDeclarationEvidence,
  buildScoredPlayEvidence,
  buildUnscoredPlayEvidence,
} from './decision-evidence';
import { selectContextualFamily } from './contextual-assessment';
import { selectFinalSeatBid } from './final-bid-selection';

const BASE_BID_THRESHOLD = 0.5;

export function playChoiceMechanism(
  nearEqualCount: number,
  preferredCandidateCount: number,
  temperature: number,
): Extract<
  AIActionChoiceMechanism,
  'highest-score' | 'near-equal-sample' | 'contextual-preference' | 'contextual-sample'
> {
  if (preferredCandidateCount < nearEqualCount) {
    if (preferredCandidateCount === 1 || temperature <= 0) return 'contextual-preference';
    if (preferredCandidateCount > 1 && temperature > 0) return 'contextual-sample';
    return 'contextual-preference';
  }
  if (nearEqualCount > 1 && temperature > 0) return 'near-equal-sample';
  return 'highest-score';
}

export interface ProfiledStrategyOptions {
  profile: SerializableDifficultyProfile;
  rng: RandomGenerator;
  useBehaviorWeights?: boolean;
  behaviorModel?: BehaviorModel;
  adaptiveConfidence?: number;
  adaptiveCompletedHands?: number;
}

/** Return the exact candidate family authorized by paired confidence. */
export function confidenceCandidateFamily(
  evaluations: readonly ActionEvaluation[],
  pairedConfidence?: AIPairedPlayConfidence,
): ActionEvaluation[] {
  const actionIds = pairedConfidence?.clearLeader
    ? [pairedConfidence.leaderActionId]
    : pairedConfidence?.unresolvedActionIds ?? evaluations.map(({ action }) => actionToId(action.action));
  const actionIdSet = new Set(actionIds);
  const family = evaluations.filter(({ action }) => actionIdSet.has(actionToId(action.action)));
  if (family.length !== actionIds.length || family.length === 0) {
    throw new Error('Paired confidence did not identify a complete selection family.');
  }
  return family;
}

/**
 * Product difficulty strategy — time-bounded PIMC with profile-driven behavior.
 */
export class ProfiledAIStrategy implements AIStrategy {
  private ctx: ExecutionContext | null = null;
  private readonly profile: SerializableDifficultyProfile;
  private readonly rng: RandomGenerator;
  private readonly useBehaviorWeights: boolean;
  private readonly behaviorModel: BehaviorModel | undefined;
  private readonly adaptiveConfidence: number;
  private readonly adaptiveCompletedHands: number;

  constructor(options: ProfiledStrategyOptions) {
    this.profile = options.profile;
    this.rng = options.rng;
    this.useBehaviorWeights = options.useBehaviorWeights ?? options.profile.useBehaviorWeights;
    this.behaviorModel = options.behaviorModel;
    this.adaptiveConfidence = options.adaptiveConfidence ?? 0;
    this.adaptiveCompletedHands = options.adaptiveCompletedHands ?? 0;
  }

  chooseAction(state: GameState, validActions: ValidAction[]): ValidAction {
    return this.analyzeAction(state, validActions).action;
  }

  analyzeAction(
    state: GameState,
    validActions: ValidAction[],
    options: { deadlineAt?: number } = {},
  ): AIActionAnalysis {
    if (validActions.length === 0) {
      throw new Error('ProfiledAIStrategy: No valid actions available to choose from');
    }

    const currentPlayer = state.players[state.currentPlayer];
    if (!currentPlayer) {
      throw new Error(`ProfiledAIStrategy: Current player ${state.currentPlayer} not found`);
    }

    switch (state.phase) {
      case 'bidding':
        return this.makeBidDecision(state, validActions, options);
      case 'trump_selection':
        return this.makeTrumpDecision(currentPlayer.hand, state.currentBid, validActions);
      case 'playing':
        return this.makePlayDecision(state, validActions, options);
      default: {
        const fallback = validActions[0];
        if (!fallback) {
          throw new Error(`ProfiledAIStrategy: No fallback for phase ${state.phase}`);
        }
        return { action: fallback, status: 'fallback' };
      }
    }
  }

  private getExecutionContext(state: GameState): ExecutionContext {
    if (!this.ctx) {
      this.ctx = createExecutionContext(state.initialConfig);
    }
    return this.ctx;
  }

  private monteCarloConfig(): MonteCarloConfig {
    return {
      biddingSimulations: this.profile.biddingMinimumSamples,
      playingSimulations: this.profile.playingMinimumWorlds,
      playingMaximumWorlds: this.profile.playingMaximumWorlds,
    };
  }

  private makeBidDecision(
    state: GameState,
    validActions: ValidAction[],
    options: { deadlineAt?: number } = {},
  ): AIActionAnalysis {
    const passAction = validActions.find((validAction) => validAction.action.type === 'pass');
    const bidActions = validActions.filter((validAction) => validAction.action.type === 'bid');
    if (bidActions.length === 0) {
      const selected = passAction ?? this.requireAction(validActions, 'makeBidDecision');
      return {
        action: selected,
        status: 'scored',
        evidence: buildBidEvidence([], selected, [], 'unscored', state.bids),
      };
    }

    const ctx = this.getExecutionContext(state);
    const myTeam = state.currentPlayer % 2;
    const threshold = BASE_BID_THRESHOLD + matchEquityBidThresholdDelta(state, myTeam, this.profile.useMatchEquity);

    const evaluations = evaluateBidActionsCore(state, bidActions, state.currentPlayer, ctx, this.monteCarloConfig(), {
      rng: this.rng,
      ...(options.deadlineAt !== undefined ? { deadlineAt: options.deadlineAt } : {}),
      matchEquityMultiplier: matchEquityMultiplier(state, myTeam, this.profile.useMatchEquity),
    });

    const finalSeat = state.bids.length === state.players.length - 1;
    const tolerance = scoreToleranceForTemperature(this.profile.nearEqualMoveTemperature, 0.12);
    let selected: ValidAction;
    let nearEqualActionIds: string[] = [];
    let objective: 'match-value' | 'make-rate' | 'unscored' = 'unscored';
    if (finalSeat) {
      const choice = selectFinalSeatBid(evaluations, threshold, tolerance);
      selected = choice.selected?.action ?? passAction ?? this.requireAction(validActions, 'makeBidDecision');
      nearEqualActionIds = choice.nearEqualActionIds;
      objective = choice.selected ? 'match-value' : 'unscored';
    } else {
      const viable = evaluations.filter((evaluation) => evaluation.makeBidRate >= threshold);
      if (viable.length === 0) {
        selected = passAction ?? this.requireAction(validActions, 'makeBidDecision');
      } else {
        const chosen = selectNearEqual(
          viable.map((evaluation) => ({ item: evaluation.action, score: evaluation.makeBidRate })),
          tolerance,
          this.profile.nearEqualMoveTemperature,
          this.rng,
        );
        selected = chosen;
        const bestScore = Math.max(...viable.map((evaluation) => evaluation.makeBidRate));
        nearEqualActionIds = viable
          .filter((evaluation) => bestScore - evaluation.makeBidRate <= tolerance)
          .map((evaluation) => actionToId(evaluation.action.action));
        objective = 'make-rate';
      }
    }
    return {
      action: selected,
      status: 'scored',
      evidence: buildBidEvidence(evaluations, selected, nearEqualActionIds, objective, state.bids),
    };
  }

  private makeTrumpDecision(
    hand: GameState['players'][number]['hand'],
    bid: Bid,
    validActions: ValidAction[],
  ): AIActionAnalysis {
    const decision = evaluateDeclarationActions(
      hand,
      validActions.map(({ action }) => action),
      { bid },
    );
    const selected = decision
      ? validActions.find(({ action }) => action === decision.action)
      : undefined;
    if (selected && decision) {
      return {
        action: selected,
        status: 'scored',
        evidence: buildDeclarationEvidence(decision, validActions),
      };
    }

    return {
      action: this.requireAction(validActions, 'makeTrumpDecision'),
      status: 'fallback',
    };
  }

  private makePlayDecision(
    state: GameState,
    validActions: ValidAction[],
    options: { deadlineAt?: number },
  ): AIActionAnalysis {
    const completeTrickAction = validActions.find((validAction) => validAction.action.type === 'complete-trick');
    if (completeTrickAction) {
      return { action: completeTrickAction, status: 'forced' };
    }

    const playActions = validActions.filter((validAction) => validAction.action.type === 'play');
    if (playActions.length === 0) {
      return {
        action: this.requireAction(validActions, 'makePlayDecision'),
        status: 'fallback',
      };
    }
    if (playActions.length === 1) {
      const onlyPlay = playActions[0]!;
      return {
        action: onlyPlay,
        status: 'forced',
        nearEqualCandidateCount: 1,
        evidence: buildUnscoredPlayEvidence([onlyPlay], onlyPlay, 'forced'),
      };
    }

    const ctx = this.getExecutionContext(state);
    const myPlayerIndex = state.currentPlayer;
    const constraints = buildConstraints(state, myPlayerIndex, ctx.rules);
    const myTeam = myPlayerIndex % 2;
    const tolerance = scoreToleranceForTemperature(this.profile.nearEqualMoveTemperature, 2.5);

    const searchOptions: ProfiledSearchOptions = {
      rng: this.rng,
      matchEquityMultiplier: matchEquityMultiplier(state, myTeam, this.profile.useMatchEquity),
      exactEndgameAtTiles: this.profile.exactEndgameAtTiles,
      strategicTolerance: tolerance,
      useBehaviorWeights:
        this.useBehaviorWeights &&
        shouldUseBehaviorWeights(this.adaptiveConfidence, this.adaptiveCompletedHands, this.behaviorModel),
      ...(this.useBehaviorWeights && this.behaviorModel
        ? {
            behaviorWeights: {
              model: this.behaviorModel,
              confidence: this.adaptiveConfidence,
            },
          }
        : {}),
      ...(options.deadlineAt !== undefined ? { deadlineAt: options.deadlineAt } : {}),
    };

    const evaluationResult = evaluatePlayActionsCore(
      state,
      playActions,
      myPlayerIndex,
      constraints,
      ctx,
      this.monteCarloConfig(),
      searchOptions,
    );
    const { evaluations, pairedConfidence, stopReason } = evaluationResult;

    if (evaluations.length === 0) {
      const fallback = playActions[0]!;
      return {
        action: fallback,
        status: 'fallback',
        evidence: buildUnscoredPlayEvidence(playActions, fallback, 'fallback'),
      };
    }

    const selectionEvaluations = confidenceCandidateFamily(evaluations, pairedConfidence);
    const selectionActionIds = selectionEvaluations.map(({ action }) => actionToId(action.action));
    const selected = selectContextualFamily(
      selectionEvaluations.map((evaluation) => ({
        item: evaluation,
        score: evaluation.contractUtility,
      })),
      this.profile.nearEqualMoveTemperature,
      this.rng,
    );
    const selectionCandidateCount = selectionEvaluations.length;
    return {
      action: selected.item.action,
      status: 'scored',
      nearEqualCandidateCount: selectionCandidateCount,
      evidence: buildScoredPlayEvidence(evaluations, selected.item.action, {
        choiceMechanism: playChoiceMechanism(
          selectionCandidateCount,
          selected.preferredCandidateCount,
          this.profile.nearEqualMoveTemperature,
        ),
        tolerance,
        candidateCount: selectionCandidateCount,
        preferredCandidateCount: selected.preferredCandidateCount,
        selectionActionIds,
        ...(pairedConfidence ? { pairedConfidence } : {}),
        ...(stopReason ? { searchStopReason: stopReason } : {}),
      }),
    };
  }

  private requireAction(validActions: ValidAction[], context: string): ValidAction {
    const fallback = validActions[0];
    if (!fallback) {
      throw new Error(`ProfiledAIStrategy.${context}: No valid actions`);
    }
    return fallback;
  }
}
